import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useEffect, useMemo, useState } from "react";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setSelectedBeneficiarieRowId } from "redux/features/CreateOrder";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isToolbarVisible?: boolean;
  isPaginationVisible?: boolean;
  columnsBackground?: string;
  onRowClick?: (row: TData) => void;
  highlightRow?: boolean;
  isPageCountVisible?: boolean;
  dealUtilizationStatus?: string;
  setDealUtilizationStatus?: (status: string) => void;
  searchData?: {
    placeholder: string;
    columns: string[];
  };
  defaultSorting?: SortingState;
  onClickNextPage?: () => void;
  onClickPreviousPage?: () => void;
  onClickFirstPage?: () => void;
  onClickLastPage?: () => void;
  totalPages?: number;
  currentPage?: number;
  onClickPageSize?: (pageSize: number) => void;
  pageSize?: number;
  pageIndex?: number;
  canNextPage?: boolean;
  canPreviousPage?: boolean;
  pageCount?: number;
  getPageCount?: () => number;
  pageName?: string;
  onGotoPage?: (page: number) => void;
  hoverRow?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isPaginationVisible = true,
  columnsBackground,
  isToolbarVisible = true,
  isPageCountVisible = true,
  dealUtilizationStatus,
  setDealUtilizationStatus,
  onRowClick,
  searchData,
  highlightRow = false,
  hoverRow = false,
  defaultSorting = [],
  onClickFirstPage,
  onClickLastPage,
  onClickNextPage,
  onClickPageSize,
  onClickPreviousPage,
  onGotoPage,
  totalPages,
  currentPage,
  pageSize,
  pageIndex,
  canNextPage,
  canPreviousPage,
  getPageCount,
  pageName = "",
}: Readonly<DataTableProps<TData, TValue>>) {
  const dispatch = useAppDispatch();
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const { selected_beneficiarie_row_id } = useAppSelector(
    (state) => state.CreateOrder,
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    enableGlobalFilter: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const filteredRows = useMemo(() => {
    if (!globalFilter) return table.getRowModel().rows;

    return table.getRowModel().rows.filter((row) => {
      return searchData?.columns.some((columnName) => {
        const cellValue = row.getValue(columnName);
        return cellValue
          ?.toString()
          .toLowerCase()
          .includes(globalFilter.toLowerCase());
      });
    });
  }, [table?.getRowModel()?.rows, globalFilter, searchData?.columns]);

  useEffect(() => { }, [filteredRows]);

  useEffect(() => {
    if (
      !selectedRowId &&
      selected_beneficiarie_row_id &&
      pageName === "Add Beneficiary"
    ) {
      setSelectedRowId(selected_beneficiarie_row_id);
    }
  }, [selectedRowId, selected_beneficiarie_row_id, pageName]);

  return (
    <div className="flex flex-col h-full">
      {isToolbarVisible && (
        <div className="mb-4">
          <DataTableToolbar
            searchData={searchData}
            dealUtilizationStatus={dealUtilizationStatus}
            setDealUtilizationStatus={setDealUtilizationStatus}
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col border rounded-lg overflow-hidden">
        {/* Single Table Container with sticky header and scrollable body */}
        <div className="relative flex-1 overflow-auto">
          <Table className="w-full">
            <TableHeader
              style={{
                backgroundImage: columnsBackground ? `linear-gradient(to right, ${columnsBackground}, ${columnsBackground})` :`linear-gradient(90.77deg, #165FE3 9.22%, #2052AD 97.89%)`,
              }}
              className='sticky top-0 z-10'
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      className={`${ columnsBackground ? 'text-black-900' : 'text-white-A700'}`}
                      key={header.id}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {filteredRows?.length ? (
                filteredRows.map((row) => (
                  <TableRow
                    className={`border-none cursor-pointer ${highlightRow && selectedRowId === row.id
                        ? "bg-[#e5eff3]"
                        : hoverRow
                          ? "hover:bg-transparent"
                          : "hover:bg-blue_gray-lite-bg"
                      }`}
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => {
                      setSelectedRowId(row.id);
                      if (pageName === "Add Beneficiary") {
                        dispatch(setSelectedBeneficiarieRowId(row.id));
                      }
                      onRowClick && onRowClick(row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow className="border-none">
                  <TableCell
                    colSpan={columns?.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Sticky Footer */}
        {isPaginationVisible && (
          <div className="sticky bottom-0 bg-white border-t">
            <DataTablePagination
              isPageCountVisible={isPageCountVisible}
              table={table}
              pageCount={table.getPageCount()}
              onClickNextPage={onClickNextPage}
              onClickPreviousPage={onClickPreviousPage}
              onClickFirstPage={onClickFirstPage}
              onClickLastPage={onClickLastPage}
              totalPages={totalPages}
              currentPage={currentPage}
              onClickPageSize={onClickPageSize}
              pageSize={pageSize}
              pageIndex={pageIndex}
              canNextPage={canNextPage}
              canPreviousPage={canPreviousPage}
              getPageCount={getPageCount}
              onGotoPage={onGotoPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}